import { normalizeSearchText } from '~/utils/string'

type IdentityGetter<T> = (item: T) => Array<string | null | undefined>
type Comparator<T> = (left: T, right: T) => number

export function stableSort<T>(items: readonly T[], compare: Comparator<T>): T[] {
  return items
    .map((item, index) => ({ item, index }))
    .sort((left, right) => compare(left.item, right.item) || left.index - right.index)
    .map(({ item }) => item)
}

function getRelevanceTier(query: string, identities: Array<string | null | undefined>): number {
  const normalizedIdentities = identities
    .map((identity) => normalizeSearchText(identity ?? ''))
    .filter(Boolean)

  if (normalizedIdentities.some((identity) => identity === query)) {
    return 0
  }
  if (normalizedIdentities.some((identity) => identity.startsWith(query))) {
    return 1
  }

  const terms = query.split(' ').filter(Boolean)
  return terms.every((term) => normalizedIdentities.some((identity) => identity.includes(term)))
    ? 2
    : 3
}

export function sortByPrimaryIdentity<T>(
  items: readonly T[],
  query: string,
  getIdentities: IdentityGetter<T>,
  compareWithinTier: Comparator<T>,
): T[] {
  const normalizedQuery = normalizeSearchText(query)
  if (!normalizedQuery) {
    return stableSort(items, compareWithinTier)
  }

  const ranked = items.map((item) => ({
    item,
    tier: getRelevanceTier(normalizedQuery, getIdentities(item)),
  }))
  return stableSort(ranked, (left, right) => {
    return left.tier - right.tier || compareWithinTier(left.item, right.item)
  }).map(({ item }) => item)
}
